// ================================================================
// 管理員 API 模組
// ================================================================
// 版本：1.0
// 日期：2026-01-21
// 功能：管理員認證、使用者管理、系統統計
// ================================================================

import { apiClient } from './client';

// ================================================================
// 類型定義
// ================================================================

export interface AdminLoginRequest {
  username: string;
  password: string;
}

export interface Admin {
  admin_id: number;
  username: string;
  email: string;
  full_name: string;
  role: 'super_admin' | 'admin' | 'support';
}

export interface AdminLoginResponse {
  success: boolean;
  message: string;
  token?: string;
  admin?: Admin;
}

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
}

export interface ChangePasswordResponse {
  success: boolean;
  message: string;
}

export interface UserStats {
  total: number;
  active: number;
  suspended: number;
  new_this_week: number;
  new_this_month: number;
}

export interface QueryStats {
  total: number;
  today: number;
  this_week: number;
  this_month: number;
}

export interface TransactionStats {
  count: number;
  total_amount: number;
}

export interface DailyStats {
  new_users: number;
  queries: number;
  revenue: number;
}

export interface WeeklyTrend {
  date: string;
  queries: number;
  revenue: number;
}

export interface CategoryStats {
  category_id: string;
  count: number;
  revenue: number;
}

export interface SystemStats {
  users: UserStats;
  queries: QueryStats;
  transactions: TransactionStats;
  today: DailyStats;
  month: DailyStats;
  weekly_trend: WeeklyTrend[];
  category_stats: CategoryStats[];
}

export interface StatsResponse {
  success: boolean;
  data?: SystemStats;
  timestamp?: string;
  message?: string;
}

export interface UserListItem {
  user_id: number;
  email: string;
  full_name: string;
  phone_number: string;
  birth_time: string;
  gender: string;
  balance: number;
  total_spent: number;
  preferred_currency: string;
  is_active: number;
  is_suspended: number;
  created_at: string;
  last_login_at: string;
  query_count?: number;
}

export interface Pagination {
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

export interface UserListResponse {
  success: boolean;
  data?: {
    users: UserListItem[];
    pagination: Pagination;
  };
  message?: string;
}

export interface Transaction {
  transaction_id: number;
  type: string;
  amount: number;
  balance_after: number;
  description: string;
  created_at: string;
}

export interface Query {
  query_id: number;
  question_id: string;
  question_text: string;
  category_id: string;
  cost: number;
  created_at: string;
}

export interface UserDetail {
  user_id: number;
  email: string;
  full_name: string;
  phone_number: string;
  birth_time: string;
  gender: 'Male' | 'Female';
  balance: number;
  total_spent: number;
  debt_limit: number;
  preferred_currency: string;
  is_active: number;
  is_suspended: number;
  created_at: string;
  updated_at: string;
  last_login_at: string | null;
}

export interface UserDetailResponse {
  success: boolean;
  user?: UserDetail;
  transactions?: Transaction[];
  queries?: Query[];
  stats?: { total_queries: number; total_cost: number };
  message?: string;
}

export interface BalanceAdjustRequest {
  amount: number;
  reason: string;
}

export interface StatusChangeRequest {
  action: 'suspend' | 'activate';
  reason?: string;
}

// ================================================================
// 管理員認證 API
// ================================================================

/**
 * 管理員登入
 */
export const adminLogin = async (data: AdminLoginRequest): Promise<AdminLoginResponse> => {
  return apiClient.post('/api/admin/login', data);
};

/**
 * 管理員修改密碼
 */
export const changeAdminPassword = async (data: ChangePasswordRequest): Promise<ChangePasswordResponse> => {
  return apiClient.post('/api/admin/change-password', data);
};

// ================================================================
// 系統統計 API
// ================================================================

/**
 * 取得系統統計資料
 */
export const getSystemStats = async (): Promise<StatsResponse> => {
  return apiClient.get('/api/admin/stats');
};

// ================================================================
// 使用者管理 API
// ================================================================

/**
 * 取得使用者列表
 */
export const getUserList = async (
  page: number = 1,
  limit: number = 20,
  search?: string,
  status: 'all' | 'active' | 'suspended' = 'all'
): Promise<UserListResponse> => {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    status,
  });
  if (search) {
    params.append('search', search);
  }
  return apiClient.get(`/api/admin/users?${params.toString()}`);
};

/**
 * 取得使用者詳情
 */
export const getUserDetail = async (userId: number): Promise<UserDetailResponse> => {
  return apiClient.get(`/api/admin/users/${userId}`);
};

/**
 * 調整使用者餘額或點數
 * @param userId 使用者 ID
 * @param amount 金額（正數加值、負數扣款）
 * @param reason 調整原因
 * @param adjustType 調整類型：'balance'（現金餘額）或 'points'（體驗點數）
 */
export const adjustUserBalance = async (
  userId: number,
  amount: number,
  reason?: string,
  adjustType: 'balance' | 'points' = 'balance'
): Promise<{ success: boolean; message: string; adjust_type?: string; value_before?: number; value_after?: number }> => {
  return apiClient.post(`/api/admin/users/${userId}/balance`, {
    amount,
    reason,
    adjust_type: adjustType,
  });
};

/**
 * 管理員修改使用者出生時間與性別
 */
export const updateUserBirthTime = async (
  userId: number,
  birthTime: string,
  gender: string
): Promise<{ success: boolean; message: string }> => {
  return apiClient.post(`/api/admin/users/${userId}/birth-time`, {
    birth_time: birthTime,
    gender,
  });
};

/**
 * 管理員重設使用者密碼
 */
export const resetUserPassword = async (
  userId: number,
  newPassword: string
): Promise<{ success: boolean; message: string }> => {
  return apiClient.post(`/api/admin/users/${userId}/reset-password`, {
    new_password: newPassword,
  });
};

/**
 * 更新使用者狀態
 */
export const updateUserStatus = async (
  userId: number,
  action: 'suspend' | 'activate' | 'suspended' | 'active',
  reason?: string
): Promise<{ success: boolean; message: string; is_suspended?: number }> => {
  // 將 suspended/active 轉換為 suspend/activate
  const normalizedAction = action === 'suspended' ? 'suspend' : action === 'active' ? 'activate' : action;
  return apiClient.post(`/api/admin/users/${userId}/status`, { action: normalizedAction, reason });
};

// ================================================================
// 交易紀錄管理 API
// ================================================================

export interface AdminTransaction {
  transaction_id: number;
  user_id: number;
  type: string;
  amount: number;
  balance_before: number;
  balance_after: number;
  description: string;
  question_id: string | null;
  admin_id: number | null;
  created_at: string;
  user_email: string;
  user_name: string;
}

export interface TransactionTypeStats {
  type: string;
  count: number;
  total_amount: number;
}

export interface TransactionListResponse {
  success: boolean;
  transactions?: AdminTransaction[];
  pagination?: Pagination;
  type_stats?: TransactionTypeStats[];
  message?: string;
}

export interface TransactionFilters {
  page?: number;
  limit?: number;
  user_search?: string;
  start_date?: string;
  end_date?: string;
  type?: string;
}

/**
 * 取得所有交易紀錄（管理員）
 */
export const getTransactions = async (
  filters: TransactionFilters = {}
): Promise<TransactionListResponse> => {
  const params = new URLSearchParams();
  if (filters.page) params.append('page', filters.page.toString());
  if (filters.limit) params.append('limit', filters.limit.toString());
  if (filters.user_search) params.append('user_search', filters.user_search);
  if (filters.start_date) params.append('start_date', filters.start_date);
  if (filters.end_date) params.append('end_date', filters.end_date);
  if (filters.type) params.append('type', filters.type);

  const queryString = params.toString();
  return apiClient.get(`/api/admin/transactions${queryString ? `?${queryString}` : ''}`);
};

// ================================================================
// 匯率管理 API
// ================================================================

export interface CurrencyRate {
  currency_code: string;
  rate: number;
  is_active: boolean;
  updated_at: string;
}

export interface CurrencyRatesResponse {
  success: boolean;
  data?: CurrencyRate[];
  message?: string;
}

/**
 * 取得匯率
 */
export const getCurrencyRates = async (): Promise<CurrencyRatesResponse> => {
  return apiClient.get('/api/admin/currencies');
};

/**
 * 更新匯率
 */
export const updateCurrencyRate = async (
  currencyCode: string,
  rate: number,
  isActive: boolean = true
): Promise<{ success: boolean; message: string }> => {
  return apiClient.post(`/api/admin/currencies/${currencyCode}`, {
    rate,
    is_active: isActive,
  });
};

// ================================================================
// 管理員日誌 API
// ================================================================

export interface AdminLog {
  log_id: number;
  admin_id: number;
  action_type: string;
  target_user_id: number | null;
  details: string | null;
  ip_address: string | null;
  user_agent: string | null;
  timestamp: string;
  admin_username: string;
  admin_email: string;
  target_user_email: string | null;
  target_user_name: string | null;
}

export interface AdminLogTypeStats {
  action_type: string;
  count: number;
}

export interface AdminListItem {
  admin_id: number;
  username: string;
  email: string;
}

export interface AdminLogsResponse {
  success: boolean;
  logs?: AdminLog[];
  pagination?: Pagination;
  type_stats?: AdminLogTypeStats[];
  admins?: AdminListItem[];
  message?: string;
}

export interface AdminLogFilters {
  page?: number;
  limit?: number;
  admin_id?: number;
  action_type?: string;
  target_user_id?: number;
  start_date?: string;
  end_date?: string;
}

/**
 * 取得管理員操作日誌
 */
export const getAdminLogs = async (
  filters: AdminLogFilters = {}
): Promise<AdminLogsResponse> => {
  const params = new URLSearchParams();
  if (filters.page) params.append('page', filters.page.toString());
  if (filters.limit) params.append('limit', filters.limit.toString());
  if (filters.admin_id) params.append('admin_id', filters.admin_id.toString());
  if (filters.action_type) params.append('action_type', filters.action_type);
  if (filters.target_user_id) params.append('target_user_id', filters.target_user_id.toString());
  if (filters.start_date) params.append('start_date', filters.start_date);
  if (filters.end_date) params.append('end_date', filters.end_date);

  const queryString = params.toString();
  return apiClient.get(`/api/admin/logs${queryString ? `?${queryString}` : ''}`);
};

// ================================================================
// 系統設定 API
// ================================================================

export interface SettingValue {
  value: string;
  description: string;
  updated_at: string;
}

export interface SystemSettings {
  registration_bonus_points?: SettingValue;
  registration_bonus_days?: SettingValue;
  default_debt_limit?: SettingValue;
  debt_warning_threshold?: SettingValue;
  [key: string]: SettingValue | undefined;
}

export interface SystemSettingsResponse {
  success: boolean;
  settings?: SystemSettings;
  message?: string;
}

export interface UpdateSettingsRequest {
  settings: { [key: string]: string | number };
}

export interface UpdateSettingsResponse {
  success: boolean;
  message: string;
  updated_count?: number;
}

/**
 * 取得系統設定
 */
export const getSystemSettings = async (): Promise<SystemSettingsResponse> => {
  return apiClient.get('/api/admin/settings');
};

/**
 * 更新系統設定
 */
export const updateSystemSettings = async (
  settings: { [key: string]: string | number }
): Promise<UpdateSettingsResponse> => {
  return apiClient.post('/api/admin/settings', { settings });
};

// ================================================================
// 問題管理 API
// ================================================================

export interface Question {
  question_id: string;
  category_id: string;
  question_text: string;
  function_name: string;
  price: number;
  is_free: number;
  popularity: number;
  description: string;
  is_active: number;
}

export interface CategoryStats {
  category_id: string;
  total: number;
  active: number;
}

export interface QuestionsResponse {
  success: boolean;
  questions?: Question[];
  category_stats?: CategoryStats[];
  total?: number;
  message?: string;
}

/**
 * 取得所有問題列表（管理員）
 */
export const getAdminQuestions = async (
  category?: string
): Promise<QuestionsResponse> => {
  const params = category && category !== 'all' ? `?category=${category}` : '';
  return apiClient.get(`/api/admin/questions${params}`);
};

/**
 * 切換問題顯示狀態
 */
export const toggleQuestionStatus = async (
  questionId: string
): Promise<{ success: boolean; message: string; question_id: string; is_active: number }> => {
  return apiClient.post(`/api/admin/questions/${questionId}/toggle`);
};

/**
 * 批次更新問題狀態
 */
export const batchUpdateQuestions = async (
  questionIds: string[],
  isActive: number
): Promise<{ success: boolean; message: string; updated_count: number }> => {
  return apiClient.post('/api/admin/questions/batch-update', {
    question_ids: questionIds,
    is_active: isActive,
  });
};

// ================================================================
// 問題價格調整 API
// ================================================================

export interface PriceChange {
  question_id: string;
  old_price: number;
  new_price: number;
}

export interface UpdatePriceResponse {
  success: boolean;
  message: string;
  question_id?: string;
  old_price?: number;
  new_price?: number;
  is_free?: number;
}

export interface BatchPriceResponse {
  success: boolean;
  message: string;
  updated_count?: number;
  mode?: string;
  value?: number;
  price_changes?: PriceChange[];
}

export interface PriceCategoryStats {
  category_id: string;
  total_count: number;
  free_count: number;
  min_price: number;
  max_price: number;
  avg_price: number;
}

export interface PriceOverallStats {
  total_count: number;
  free_count: number;
  min_price: number;
  max_price: number;
  avg_price: number;
}

export interface PriceStatsResponse {
  success: boolean;
  category_stats?: PriceCategoryStats[];
  overall_stats?: PriceOverallStats;
  message?: string;
}

/**
 * 更新單一問題價格
 */
export const updateQuestionPrice = async (
  questionId: string,
  price: number,
  isFree: boolean = false
): Promise<UpdatePriceResponse> => {
  return apiClient.post(`/api/admin/questions/${questionId}/price`, {
    price,
    is_free: isFree ? 1 : 0,
  });
};

/**
 * 批次調整問題價格
 * @param mode 調整模式: "fixed" (設定固定價格), "percent" (百分比調整), "amount" (金額調整)
 * @param value 調整值
 * @param questionIds 問題ID陣列（可選）
 * @param categoryId 類別ID（可選，若提供則調整整個類別）
 */
export const batchUpdatePrice = async (
  mode: 'fixed' | 'percent' | 'amount',
  value: number,
  questionIds?: string[],
  categoryId?: string
): Promise<BatchPriceResponse> => {
  return apiClient.post('/api/admin/questions/batch-price', {
    mode,
    value,
    question_ids: questionIds,
    category_id: categoryId,
  });
};

/**
 * 取得價格統計
 */
export const getPriceStats = async (): Promise<PriceStatsResponse> => {
  return apiClient.get('/api/admin/questions/price-stats');
};

// ================================================================
// 系統公告 API
// ================================================================

export interface Announcement {
  announcement_id: number;
  title: string;
  content: string;
  type: 'info' | 'warning' | 'success' | 'error';
  priority: number;
  is_active: number;
  start_date: string | null;
  end_date: string | null;
  created_by: number | null;
  created_by_name?: string;
  created_at: string;
  updated_at: string;
}

export interface AnnouncementStats {
  total: number;
  active: number;
  inactive: number;
}

export interface AnnouncementsResponse {
  success: boolean;
  announcements?: Announcement[];
  stats?: AnnouncementStats;
  count?: number;
  message?: string;
}

export interface CreateAnnouncementRequest {
  title: string;
  content: string;
  type?: 'info' | 'warning' | 'success' | 'error';
  priority?: number;
  is_active?: number;
  start_date?: string;
  end_date?: string;
}

export interface UpdateAnnouncementRequest {
  title?: string;
  content?: string;
  type?: 'info' | 'warning' | 'success' | 'error';
  priority?: number;
  is_active?: number;
  start_date?: string;
  end_date?: string;
}

/**
 * 取得目前有效公告（公開 API）
 */
export const getActiveAnnouncements = async (): Promise<AnnouncementsResponse> => {
  return apiClient.get('/api/announcements');
};

/**
 * 取得所有公告（管理員）
 */
export const getAdminAnnouncements = async (): Promise<AnnouncementsResponse> => {
  return apiClient.get('/api/admin/announcements');
};

/**
 * 新增公告（管理員）
 */
export const createAnnouncement = async (
  data: CreateAnnouncementRequest
): Promise<{ success: boolean; message: string; announcement_id?: number }> => {
  return apiClient.post('/api/admin/announcements', data);
};

/**
 * 更新公告（管理員）
 */
export const updateAnnouncement = async (
  announcementId: number,
  data: UpdateAnnouncementRequest
): Promise<{ success: boolean; message: string }> => {
  return apiClient.put(`/api/admin/announcements/${announcementId}`, data);
};

/**
 * 刪除公告（管理員）
 */
export const deleteAnnouncement = async (
  announcementId: number
): Promise<{ success: boolean; message: string }> => {
  return apiClient.delete(`/api/admin/announcements/${announcementId}`);
};

/**
 * 切換公告狀態（管理員）
 */
export const toggleAnnouncementStatus = async (
  announcementId: number
): Promise<{ success: boolean; message: string; is_active: number }> => {
  return apiClient.post(`/api/admin/announcements/${announcementId}/toggle`);
};

// ================================================================
// 備份與還原相關 API
// ================================================================

export interface BackupInfo {
  backup_id: string;
  created_at: string;
  total_size: number;
  total_size_mb: number;
  databases: { [key: string]: { size: number; mtime: string } };
  is_compressed: boolean;
  path: string;
}

export interface BackupListResponse {
  success: boolean;
  backups: BackupInfo[];
  total_count: number;
  message: string;
}

export interface CreateBackupRequest {
  include_optional?: boolean;
  backup_name?: string;
  compress?: boolean;
}

export interface CreateBackupResponse {
  success: boolean;
  backup_id?: string;
  backup_path?: string;
  total_size?: number;
  databases?: string[];
  failed_databases?: string[];
  message: string;
}

export interface RestoreBackupRequest {
  databases?: string[];
  create_backup_before_restore?: boolean;
}

export interface RestoreBackupResponse {
  success: boolean;
  restored_databases?: string[];
  failed_databases?: string[];
  message: string;
}

export interface BackupDetailResponse {
  success: boolean;
  backup_info?: any;
  message: string;
}

export interface ValidateBackupResponse {
  success: boolean;
  all_valid?: boolean;
  validation_results?: Array<{
    database: string;
    valid: boolean;
    message: string;
  }>;
  message: string;
}

/**
 * 建立資料庫備份（管理員）
 */
export const createBackup = async (
  data: CreateBackupRequest = {}
): Promise<CreateBackupResponse> => {
  return apiClient.post('/api/admin/backup/create', data);
};

/**
 * 取得備份列表（管理員）
 */
export const listBackups = async (): Promise<BackupListResponse> => {
  return apiClient.get('/api/admin/backups');
};

/**
 * 取得備份詳情（管理員）
 */
export const getBackupInfo = async (backupId: string): Promise<BackupDetailResponse> => {
  return apiClient.get(`/api/admin/backup/${backupId}`);
};

/**
 * 驗證備份完整性（管理員）
 */
export const validateBackup = async (backupId: string): Promise<ValidateBackupResponse> => {
  return apiClient.post(`/api/admin/backup/${backupId}/validate`);
};

/**
 * 還原資料庫（管理員，需要 Super Admin 權限）
 */
export const restoreBackup = async (
  backupId: string,
  data: RestoreBackupRequest = {}
): Promise<RestoreBackupResponse> => {
  return apiClient.post(`/api/admin/backup/${backupId}/restore`, data);
};

/**
 * 刪除備份（管理員）
 */
export const deleteBackup = async (
  backupId: string
): Promise<{ success: boolean; message: string }> => {
  return apiClient.delete(`/api/admin/backup/${backupId}`);
};
